import { Injectable } from "@angular/core";

import { IAppConfig, Guid, IEnvironment, Role } from "../models";
import { BehaviorSubject } from "rxjs";

export interface IAnalyticsContext {
  teamName?: string;
  domain?: string;
}

declare global {
  interface Window {
    // Stamped into the page by the server ahead of the app scripts; absent on
    // dev-served pages (ng serve) and any host that doesn't inject it.
    appConfig?: IAppConfig;
  }
}

/**
 * Release stage for error/performance telemetry (Bugsnag). Called at module
 * load, before Angular DI is up, so it reads window.appConfig directly (the
 * server stamps it into the page ahead of the app scripts). The server
 * derives the stage from its host environment; the same prod build artifact
 * deploys to staging and production, so only the server knows which this is.
 * Dev-served pages (ng serve) have no window.appConfig and fall back to
 * "development".
 */
export function getReleaseStage(env: IEnvironment): string {
  if (!env.production) {
    return "development";
  }
  return window.appConfig?.releaseStage || "production";
}

@Injectable({ providedIn: "root" })
export class AppConfigService implements Required<IAppConfig> {
  // is this a production build
  public production = true;

  // AppName
  public appName: string | null = null;

  // Version
  public version: string | null = null;

  // Short git commit hash of this Angular bundle (baked in at build time)
  public commit: string | null = null;

  // Commit of the running server build (from window.appConfig). Distinct from
  // commit above: the server and SPA deploy separately. The commit alone
  // identifies the server (no managed version scheme).
  public serverCommit: string | null = null;

  // Hotjar Id
  public hotjarId: number | null = null;

  // Google Analytics Key
  public gaKey: string | null = null;

  // Application insights key
  public aiKey: Guid | null = null;

  // Active directory app id
  public adKey: Guid | null = null;

  // CdnOrigin
  public cdnOrigin: string | null = null;

  // Telemetry release stage from the server ("development" / "staging" /
  // "production"); null when serving without window.appConfig (ng serve)
  public releaseStage: string | null = null;

  // Role capability hierarchy (server's RoleHelper.GetEffectiveRoles).
  // Keys are RoleFlag ints as strings. Read-only mirror; never re-derived.
  public effectiveRoles: Record<string, number[]> | null = null;

  // TeamName and SelfServDomain (for analytics)
  // Observable provided in addition to property so that analytics service can subscribe
  private readonly _analyticsContextSource =
    new BehaviorSubject<IAnalyticsContext | null>(null);

  public readonly analyticsContext$ =
    this._analyticsContextSource.asObservable();

  public setAnalyticsContext(value: IAnalyticsContext) {
    this._analyticsContextSource.next(value);
  }

  public init(appName: string, env: IEnvironment): void {
    this.appName = appName;
    this.version = env.version;
    this.commit = env.commit || null;
    this.production = env.production;

    // get global appConfig object (injected by the host page; may be absent)
    let appConfig = window.appConfig;

    if (!appConfig) {
      // display warning in production
      if (this.production) {
        console.error("window.appConfig is not defined");
      }
      appConfig = {};
    }

    this.gaKey = appConfig.gaKey || env.gaKey || null;
    this.hotjarId = appConfig.hotjarId || env.hotjarId || null;

    this.adKey = appConfig.adKey || env.adKey || null;
    this.aiKey = appConfig.aiKey || null;
    this.cdnOrigin = appConfig.cdnOrigin || "";
    this.releaseStage = appConfig.releaseStage || null;
    this.serverCommit = appConfig.serverCommit || null;
    this.effectiveRoles = appConfig.effectiveRoles || null;
  }

  /**
   * The effective roles a single assigned role grants, per the server
   * hierarchy. Falls back to the role itself if the map is unavailable.
   * Role values are coerced to numbers throughout: form controls can hold
   * role values as strings, and the served map keys/values are numeric, so
   * mixing the two would silently break lookups and Set membership.
   */
  public getEffectiveRoles(role: Role): Role[] {
    const key = Number(role);
    const list = this.effectiveRoles?.[String(key)];
    return list && list.length ? list.map((r) => Number(r)) : [key];
  }

  /**
   * Roles that the given explicit selection implies but does not itself
   * contain (e.g. selecting SiteAdmin implies AdviceAdmin/TeamAdmin/...).
   * Distinct; excludes anything already explicitly selected.
   */
  public getImpliedRoles(selected: Role[]): Role[] {
    const explicit = new Set<number>(selected.map((r) => Number(r)));
    const implied = new Set<number>();
    for (const role of explicit) {
      for (const eff of this.getEffectiveRoles(role)) {
        if (!explicit.has(Number(eff))) {
          implied.add(Number(eff));
        }
      }
    }
    return [...implied] as Role[];
  }
}
